import { c as _c } from "react/compiler-runtime";
import { ThemedLogoFpMark } from '@onefootprint/icons';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import WaveText, { type WaveWord } from './wave-text';
type TimeOfDay = 'morning' | 'afternoon' | 'evening';
const getTimeOfDay = (date: Date): TimeOfDay => {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
};

// Trust 3A is the brand's display face, declared app-wide in auth-brand.css. It ships one weight,
// so anything heavier than regular would be synthesised. On desktop the size follows the viewport
// so the longest headline stays on one line as the home cycles.
const displayClassName = "font-['Trust_3A',Georgia,serif] font-normal tracking-[-0.015em] text-balance text-primary text-[1.75rem] leading-[1.3] md:text-[clamp(1.75rem,3.1vw,2.5rem)]";
type PercyHeroProps = {
  /** What screen readers announce, and the visible title when there is no greeting. */
  title: string;
  subtitle?: string;
  /** The home greets by time of day; a headline, once the cycle reaches one, replaces the greeting. */
  greeting?: {
    name?: string;
    headline?: string;
  };
};
const toWords = (text: string, muted?: boolean): WaveWord[] => text.split(' ').filter(Boolean).map(word => ({
  text: word,
  muted
}));
const PercyHero = t0 => {
  const $ = _c(22);
  const {
    title,
    subtitle,
    greeting
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "home"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("ask-percy", t1);
  const reduceMotion = useReducedMotion() ?? false;
  let t2;
  if ($[1] !== greeting?.headline || $[2] !== t) {
    t2 = greeting?.headline ?? t(_temp);
    $[1] = greeting?.headline;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const phrase = t2;
  let t3;
  if ($[4] !== greeting || $[5] !== phrase) {
    t3 = greeting?.name && !greeting.headline ? [...toWords(`${phrase},`), ...toWords(greeting.name, true)] : toWords(phrase);
    $[4] = greeting;
    $[5] = phrase;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const words = t3;
  let t4;
  if ($[7] !== reduceMotion) {
    t4 = reduceMotion ? false : {
      opacity: 0,
      scale: 0.9
    };
    $[7] = reduceMotion;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 1,
      scale: 1
    };
    t6 = {
      duration: 0.6,
      ease: "easeOut"
    };
    t7 = <ThemedLogoFpMark color="primary" className="size-12 md:size-14" />;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4) {
    t8 = <motion.div initial={t4} animate={t5} transition={t6}>{t7}</motion.div>;
    $[12] = t4;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== greeting || $[15] !== subtitle || $[16] !== title || $[17] !== words) {
    t9 = greeting ? <><h1 className="sr-only">{title}</h1><p aria-hidden={true} className={`w-full max-w-[800px] max-md:flex max-md:min-h-[2lh] max-md:items-center max-md:justify-center ${displayClassName}`}><WaveText words={words} /></p></> : <div className="flex max-w-[560px] flex-col items-center gap-2"><h1 className={displayClassName}>{title}</h1>{subtitle && <p className="text-body-2 text-tertiary">{subtitle}</p>}</div>;
    $[14] = greeting;
    $[15] = subtitle;
    $[16] = title;
    $[17] = words;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex w-full flex-col items-center gap-1 text-center md:gap-5">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
export default PercyHero;
function _temp($) {
  return $.greeting[getTimeOfDay(new Date())];
}